<template>
  <div
      class="bg-white border-b border-neutral-200 px-4 py-2 flex items-center justify-between overflow-x-auto whitespace-nowrap uppercase">

    <div v-if="menuItems" class="hidden md:flex items-center space-x-6 text-sm font-medium m-auto">
      <template v-if="hasMoreToLeft">
        <UIcon class="size-6 cursor-pointer" name="i-lucide-chevron-left" @click="start -= 1"/>
      </template>

      <div class="flex space-x-6 px-5">
        <NuxtLink v-for="menuItem in menuItems.slice(start, visibleItems + start)" :key="menuItem.value"
                  :to="`/${shophandle}`"
                  class="hover:text-black text-gray-700 cursor-pointer"
                  @click="() => selectCategory(menuItem)">
          <p :class="{'underline': menuItem.value == categoryId}">{{ menuItem.label }}</p>
        </NuxtLink>
      </div>

      <template v-if="hasMoreToRight">
        <UIcon class="size-6 cursor-pointer" name="i-lucide-chevron-right" @click="start += 1"/>
      </template>

    </div>

    <UDropdownMenu :items="menuItems" arrow class="md:hidden">
      <UButton class="cursor-pointer bg-white text-black hover:bg-white">
        {{ activeCategoryName }}
        <Icon class="w-4 h-4" name="ion:chevron-down-outline"/>
      </UButton>

      <template #item="{item}">
        <NuxtLink :to="`/${shophandle}`" @click="() => selectCategory(item)">{{ item.label }}</NuxtLink>
      </template>

    </UDropdownMenu>

    <SortBy/>

  </div>

</template>

<script lang="ts" setup>
import {useProductsListStore} from "~/store/productsList";
import type {DropdownMenuItem} from "@nuxt/ui"
import SortBy from "~/components/SortBy.vue";
import {useTypesenseStore} from "~/store/typesense";

const {shophandle} = useRoute().params
const ignore = [
  'Apparel', 'Sport', 'More'
]
const {setCategory} = useProductsListStore()
const {categoryId} = storeToRefs(useProductsListStore())
const activeCategoryName = ref<string>("All Products")
const start = ref<number>(0)
const linksContainer = ref<HTMLDivElement>()

const {data: categories} = await useAsyncData(
    'categories',
    () => useApiFetch('public/categories'),
    {
      transform: ({data}) => data.value.filter(
          c => !ignore.includes(c.name)
      ).reduce(
          (acc, category) => {
            acc[category.id] = category
            return acc
          },
          {}
      )
    }
)

const {width} = useWindowSize()

const breakpoints = [
  {minWidth: 0, count: 3},
  {minWidth: 770, count: 4},
  {minWidth: 1024, count: 5},
  {minWidth: 1280, count: 6},
  {minWidth: 1400, count: 8},
];

const visibleItems = computed(() => {
  for (let i = breakpoints.length - 1; i >= 0; i--) {
    if (width.value >= breakpoints[i].minWidth) {
      return breakpoints[i].count;
    }
  }
  return 3
});


const hasMoreToRight = computed(() => {
  return start.value + visibleItems.value < menuItems.value.length
})

const hasMoreToLeft = computed(() => {
  return start.value > 0
})


const {data: categoriesCount} = await useAsyncData(
    'categories-counts',
    async () => {
      if (!shophandle) {
        return []
      }
      const typesense = await useTypesense()
      const {isExpired, fetchKey} = useTypesenseStore()
      if (isExpired) {
        await fetchKey()
      }
      // check if key is not expired

      return await typesense
          .collections('products')
          .documents()
          .search({
            filter_by: `shop:${shophandle}`,
            q: '*',
            facet_by: 'category_id',
            limit: 1,
            page: 1
          })
    },
    {
      transform: result => result.facet_counts[0].counts
    }
)


const menuItems = computed(
    () => {
      if (!categories.value || !categoriesCount.value || categoriesCount.value.length === 0) {
        return []
      }
      return [
        {
          label: 'All Products',
          value: null,
          type: "link"
        },
        ...categoriesCount.value.map(
            (facet) => ({
              label: categories.value[facet.value].name,
              value: facet.value,
              type: "link"
            }) as DropdownMenuItem
        )
      ]
    }
)

const selectCategory = (menuItem: DropdownMenuItem) => {
  setCategory(menuItem.value)
  activeCategoryName.value = menuItem.label
}

</script>